/**
 * videoEmbed — small helper for converting worker-submitted video links
 * (YouTube / Google Drive / Vimeo / Dropbox / other) into embeddable iframe
 * URLs. Shared by the worker, employer, and admin dashboards so the embed
 * behaviour is identical across roles.
 *
 *   detectPlatform(url)  -> 'youtube' | 'google_drive' | 'vimeo' | 'dropbox' | 'other'
 *   toEmbed(url, plat?)  -> { embedUrl, kind: 'iframe' | 'video' | 'link' }
 *   isValidUrl(url)      -> boolean (http/https + at least one dot)
 *   truncate(url, n)     -> display-friendly short version
 */

export function detectPlatform(rawUrl) {
    const url = (rawUrl || '').toLowerCase();
    if (!url) return 'other';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('drive.google.com')) return 'google_drive';
    if (url.includes('vimeo.com')) return 'vimeo';
    if (url.includes('dropbox.com')) return 'dropbox';
    return 'other';
}

export function isValidUrl(rawUrl) {
    const url = (rawUrl || '').trim();
    if (!url) return false;
    if (!/^https?:\/\//i.test(url)) return false;
    if (!url.includes('.')) return false;
    return true;
}

function extractYouTubeId(url) {
    // Handles youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>, youtube.com/shorts/<id>
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtu.be')) {
            return u.pathname.replace(/^\//, '').split('/')[0] || null;
        }
        if (u.pathname.startsWith('/watch')) {
            return u.searchParams.get('v');
        }
        if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) {
            return u.pathname.split('/')[2] || null;
        }
    } catch { /* invalid URL */ }
    return null;
}

function extractDriveId(url) {
    // Handles drive.google.com/file/d/<id>/view and ?id=<id>
    try {
        const u = new URL(url);
        const m = u.pathname.match(/\/file\/d\/([^/]+)/);
        if (m) return m[1];
        if (u.searchParams.get('id')) return u.searchParams.get('id');
    } catch { /* invalid URL */ }
    return null;
}

function extractVimeoId(url) {
    try {
        const u = new URL(url);
        const m = u.pathname.match(/\/(\d+)/);
        if (m) return m[1];
    } catch { /* invalid URL */ }
    return null;
}

/**
 * toEmbed — returns { embedUrl, kind } where:
 *   kind = 'iframe'  → render <iframe src={embedUrl} />
 *   kind = 'video'   → render <video src={embedUrl} controls /> (Dropbox)
 *   kind = 'link'    → fallback; just show "Open in new tab" button
 */
export function toEmbed(rawUrl, platformHint) {
    const url = (rawUrl || '').trim();
    if (!url) return { embedUrl: null, kind: 'link' };
    const platform = platformHint || detectPlatform(url);

    if (platform === 'youtube') {
        const id = extractYouTubeId(url);
        if (id) return { embedUrl: `https://www.youtube.com/embed/${id}`, kind: 'iframe' };
    }
    if (platform === 'google_drive') {
        const id = extractDriveId(url);
        if (id) return { embedUrl: `https://drive.google.com/file/d/${id}/preview`, kind: 'iframe' };
    }
    if (platform === 'vimeo') {
        const id = extractVimeoId(url);
        if (id) return { embedUrl: `https://player.vimeo.com/video/${id}`, kind: 'iframe' };
    }
    if (platform === 'dropbox') {
        // Dropbox: ?dl=0 → ?raw=1 forces direct file streaming.
        const converted = url.includes('?dl=0')
            ? url.replace('?dl=0', '?raw=1')
            : (url.includes('?')
                ? url.replace(/\?(.+)$/, '?raw=1')
                : `${url}?raw=1`);
        return { embedUrl: converted, kind: 'video' };
    }
    return { embedUrl: url, kind: 'link' };
}

export function truncate(url, max = 50) {
    if (!url) return '';
    if (url.length <= max) return url;
    return `${url.slice(0, max)}…`;
}
